import { FC, useRef, useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Stepper, StepperItem, StepperWC } from '@pypestream/design-system';
import { FieldValues, FormProvider } from 'react-hook-form';

import { GenericModal, GenericModalProps } from '..';
import { useSolutionWizardForm } from './use-solution-wizard-form';
import { StepInitial } from './step-initial';
import { StepConfig } from './step-config';
import { StepTasks } from './step-tasks';
import { StepAgent } from './step-agent';
import { StepCreateAiProvider } from './step-create-ai-provider';
import { StepLoading } from './step-loading';

import './create-solution-modal.scss';

export type CreateSolutionModalProps = GenericModalProps;

export const CreateSolutionModal: FC<CreateSolutionModalProps> = ({
  modalRef,
  onClose: _onClose,
}) => {
  const swiperRef = useRef<StepperWC>(null);

  const {
    initialStepForm,
    configStepForm,
    tasksStepForm,
    agentStepForm,
    uploadedFiles,
    handleNextStep,
    createAiProviderStepForm,
    handlePrevStep,
    addFile,
    removeFile,
    createSolution,
    resetFormState,
    credentials,
    providers,
    isLoading,
    setIsLoading,
    isBotFlowError,
    stepBeforeLoadingRef,
  } = useSolutionWizardForm(swiperRef);

  const [activeStepName, setActiveStepName] = useState('create');

  const onClose = () => {
    _onClose?.();
    resetFormState();
  };

  useEffect(() => {
    if (!isBotFlowError) return;

    setIsLoading(false);
    swiperRef?.current?.goToStep(stepBeforeLoadingRef.current);
  }, [isBotFlowError, setIsLoading, swiperRef, stepBeforeLoadingRef]);

  const onCreateSolution = (values: FieldValues) => {
    createSolution({
      callback: () => {
        _onClose?.();
      },
      formData: values,
    });
  };

  const hasCredentials = useMemo(
    () => credentials && credentials.length > 0,
    [credentials]
  );

  const handleStepperChange = (event: CustomEvent) => {
    setActiveStepName(event.detail.screenName);
  };

  return (
    <>
      {createPortal(
        <GenericModal
          test-id="create-solution-modal"
          modalRef={modalRef}
          onClose={onClose}
          onError={() => setIsLoading(false)}
          hideCloseBtn={isLoading}
          size="large"
          usePortal={false}
          stayOnClickOutside={isLoading}
          stayOnEsc={isLoading}
          className="c-create-solution-modal"
        >
          <Stepper
            id="stepper"
            ref={swiperRef}
            // @ts-expect-error stepper.wc ref issue
            onStepChange={handleStepperChange}
          >
            <StepperItem name="create">
              <FormProvider {...initialStepForm}>
                <StepInitial
                  nextStep={(data) => handleNextStep(data)}
                  createSolution={createSolution}
                  isLoading={isLoading}
                  credentials={credentials}
                  onSuccess={() => {
                    _onClose?.();
                  }}
                  disabled={isLoading}
                />
              </FormProvider>
            </StepperItem>
            {!hasCredentials && (
              <StepperItem name="create-ai-provider">
                <FormProvider {...createAiProviderStepForm}>
                  <StepCreateAiProvider
                    nextStep={(data) => handleNextStep(data, true)}
                    backStep={handlePrevStep}
                    providers={providers}
                  />
                </FormProvider>
              </StepperItem>
            )}
            <StepperItem name="config">
              <FormProvider {...configStepForm}>
                <StepConfig
                  nextStep={(data) => handleNextStep(data)}
                  backStep={handlePrevStep}
                  disabled={isLoading}
                />
              </FormProvider>
            </StepperItem>
            <StepperItem name="agent">
              <FormProvider {...agentStepForm}>
                <StepAgent
                  nextStep={(data) => handleNextStep(data)}
                  backStep={handlePrevStep}
                  uploadedFiles={uploadedFiles}
                  addFile={addFile}
                  removeFile={removeFile}
                  disabled={isLoading}
                />
              </FormProvider>
            </StepperItem>
            <StepperItem
              test-id="create-solution-modal-tasks-stepper-item"
              name="tasks"
            >
              <FormProvider {...tasksStepForm}>
                <StepTasks
                  backStep={handlePrevStep}
                  onSubmit={onCreateSolution}
                  disabled={isLoading}
                />
              </FormProvider>
            </StepperItem>
            <StepperItem
              test-id="create-solution-modal-loading-stepper-item"
              name="loading"
            >
              <StepLoading active={activeStepName === 'loading'} />
            </StepperItem>
          </Stepper>
        </GenericModal>,
        document.body
      )}
    </>
  );
};
